'use client';

import { loadJS } from '@ahm/common-helpers';
import { useCallback, useEffect, useRef } from 'react';
import { useAuthStore } from '@/store/use-auth-store';
import type { AuthResponse, FBAuthResponse } from '@/types/auth.types';

declare global {
  const FB: any;
  interface Window {
    fbAsyncInit: () => void;
  }
}

const isProd = process.env.NODE_ENV === 'production';
const USER_INFO_FIELDS = 'name,email,picture';
const FAILED_USER_INFO_MESSAGE = 'Failed to get user info:';

// Global timeout tracking to prevent memory leaks (removed for simplicity)
// Individual timeouts are now managed within their respective promises

// Circuit breaker for Facebook API calls
let consecutiveFailures = 0;
const MAX_CONSECUTIVE_FAILURES = 3;
const CIRCUIT_BREAKER_RESET_TIME = 30000; // 30 seconds
let circuitBreakerOpenTime: number | null = null;

const isCircuitBreakerOpen = (): boolean => {
  if (
    circuitBreakerOpenTime &&
    Date.now() - circuitBreakerOpenTime > CIRCUIT_BREAKER_RESET_TIME
  ) {
    // Reset circuit breaker after timeout
    circuitBreakerOpenTime = null;
    consecutiveFailures = 0;
    return false;
  }
  return circuitBreakerOpenTime !== null;
};

const recordFailure = () => {
  consecutiveFailures++;
  if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
    circuitBreakerOpenTime = Date.now();
    console.warn(
      'Facebook API circuit breaker opened due to consecutive failures'
    );
  }
};

const recordSuccess = () => {
  consecutiveFailures = 0;
  circuitBreakerOpenTime = null;
};

// Cleanup function to clear all timeouts (useful for testing or app cleanup)
export const clearAllFacebookTimeouts = () => {
  // This is a no-op function for backward compatibility
  // Actual timeout cleanup is handled within each promise
};

export const useFacebookSDK = ({
  locale = 'vi_VN',
  autoRestore = false,
}: {
  locale?: 'vi_VN' | 'en_US';
  autoRestore?: boolean;
} = {}) => {
  const { facebookAppId, facebookAppVersion, setIsLoading, login } =
    useAuthStore();

  // Flag to prevent multiple initialization calls
  const isInitializedRef = useRef(false);
  const isRestoringSessionRef = useRef(false);
  // Debounce refs to prevent rapid successive calls
  const lastInitCallRef = useRef(0);
  const DEBOUNCE_TIME = 1000; // 1 second debounce

  const initializeFb = useCallback((): Promise<AuthResponse | null> => {
    if (!facebookAppId) {
      console.warn('Facebook App ID is not configured');
      return Promise.resolve(null);
    }

    // Check circuit breaker
    if (isCircuitBreakerOpen()) {
      console.warn(
        'Facebook API circuit breaker is open, skipping initialization'
      );
      return Promise.resolve(null);
    }

    // Debounce rapid calls
    const now = Date.now();
    if (now - lastInitCallRef.current < DEBOUNCE_TIME) {
      console.warn('Facebook initialization called too rapidly, debouncing');
      return Promise.resolve(null);
    }
    lastInitCallRef.current = now;

    // Prevent multiple initialization calls
    if (isInitializedRef.current) {
      return Promise.resolve(null);
    }

    const src = isProd
      ? `https://connect.facebook.net/${locale}/sdk.js`
      : `https://connect.facebook.net/${locale}/sdk/debug.js`;

    const attr = {
      id: 'facebook-jssdk',
    };

    return new Promise((resolve, reject) => {
      setIsLoading(true);
      isInitializedRef.current = true;

      // Add timeout for the entire initialization process
      const initTimeout = setTimeout(() => {
        setIsLoading(false);
        isInitializedRef.current = false;
        reject(new Error('Facebook SDK initialization timeout'));
      }, 10000); // 10 seconds timeout

      loadJS(src, attr)
        .then(() => {
          try {
            (FB as any).init({
              appId: facebookAppId,
              cookie: true,
              xfbml: true,
              version: facebookAppVersion ?? 'v20.0',
            });

            // Wrap logPageView in try-catch with timeout to prevent hanging
            try {
              const pageViewTimeout = setTimeout(() => {
                console.warn(
                  'Facebook PageView event timeout - continuing initialization'
                );
              }, 3000); // 3 seconds timeout for pageview

              (FB as any).AppEvents.logPageView();
              clearTimeout(pageViewTimeout);
              recordSuccess(); // Record successful pageview
            } catch (pageViewError) {
              console.warn('Facebook PageView failed:', pageViewError);
              recordFailure(); // Record failed pageview
              // Continue initialization even if pageview fails
            }

            // Only check login status during auto-restore to avoid duplicate calls
            if (autoRestore && !isRestoringSessionRef.current) {
              isRestoringSessionRef.current = true;
              // Check if user is already logged in with timeout
              const loginStatusTimeout = setTimeout(() => {
                setIsLoading(false);
                isRestoringSessionRef.current = false;
                clearTimeout(initTimeout);
                resolve(null);
              }, 5000); // 5 seconds timeout for login status check

              try {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                (FB as any).getLoginStatus((res: FBAuthResponse) => {
                  clearTimeout(loginStatusTimeout);
                  if (res.status === 'connected' && res.authResponse) {
                    // User is already logged in, get their info and update the store
                    const userInfoTimeout = setTimeout(() => {
                      setIsLoading(false);
                      isRestoringSessionRef.current = false;
                      clearTimeout(initTimeout);
                      resolve(res.authResponse);
                    }, 3000); // 3 seconds timeout for user info

                    try {
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                      (FB as any).api(
                        '/me',
                        { fields: USER_INFO_FIELDS },
                        (userInfo: any) => {
                          clearTimeout(userInfoTimeout);
                          if (userInfo && !userInfo.error) {
                            login(res.authResponse, userInfo);
                            setIsLoading(false);
                            isRestoringSessionRef.current = false;
                            clearTimeout(initTimeout);
                            resolve(res.authResponse);
                            console.log(
                              'Facebook SDK initialized and user is logged in:',
                              res
                            );
                          } else {
                            console.error(
                              FAILED_USER_INFO_MESSAGE,
                              userInfo?.error
                            );
                            setIsLoading(false);
                            isRestoringSessionRef.current = false;
                            clearTimeout(initTimeout);
                            resolve(res.authResponse);
                          }
                        }
                      );
                    } catch (userInfoError) {
                      clearTimeout(userInfoTimeout);
                      console.error('Error getting user info:', userInfoError);
                      setIsLoading(false);
                      isRestoringSessionRef.current = false;
                      clearTimeout(initTimeout);
                      resolve(res.authResponse);
                    }
                  } else {
                    setIsLoading(false);
                    isRestoringSessionRef.current = false;
                    clearTimeout(initTimeout);
                    resolve(null);
                    console.log(
                      'Facebook SDK initialized but user is not logged in'
                    );
                  }
                });
              } catch (loginStatusError) {
                clearTimeout(loginStatusTimeout);
                console.error('Error checking login status:', loginStatusError);
                setIsLoading(false);
                isRestoringSessionRef.current = false;
                clearTimeout(initTimeout);
                resolve(null);
              }
            } else {
              setIsLoading(false);
              clearTimeout(initTimeout);
              resolve(null);
              console.log(
                'Facebook SDK initialized without login status check'
              );
            }
          } catch (initError) {
            console.error(
              'Error during Facebook SDK initialization:',
              initError
            );
            setIsLoading(false);
            isInitializedRef.current = false;
            clearTimeout(initTimeout);
            reject(initError);
          }
        })
        .catch((error) => {
          setIsLoading(false);
          isInitializedRef.current = false;
          clearTimeout(initTimeout);
          console.error('Cannot load Facebook SDK:', error);
          reject(error);
        });
    });
  }, [
    facebookAppId,
    facebookAppVersion,
    locale,
    setIsLoading,
    autoRestore,
    login,
  ]);

  const loginWithFacebook = useCallback((): Promise<AuthResponse | null> => {
    if (!facebookAppId) {
      console.warn('Facebook App ID is not configured');
      return Promise.resolve(null);
    }

    return new Promise((resolve, reject) => {
      setIsLoading(true);

      // Set timeout to prevent infinite loading state
      const loginTimeout = setTimeout(() => {
        console.warn('Facebook login timeout - resetting loading state');
        setIsLoading(false);
        resolve(null);
      }, 15000); // 15 seconds timeout

      // Track if login process is completed to avoid multiple timeout calls
      let isLoginCompleted = false;

      const completeLogin = (result: AuthResponse | null, error?: Error) => {
        if (isLoginCompleted) return;
        isLoginCompleted = true;
        clearTimeout(loginTimeout);
        setIsLoading(false);

        if (error) {
          reject(error);
        } else {
          resolve(result);
        }
      };

      // Listen for window focus to handle cases where user closes popup and returns
      const handleWindowFocus = () => {
        if (!isLoginCompleted) {
          // Give a small delay to allow Facebook SDK to process any pending responses
          setTimeout(() => {
            if (!isLoginCompleted) {
              console.log(
                'User returned to window without completing Facebook login'
              );
              completeLogin(null);
            }
          }, 1000);
        }
      };

      window.addEventListener('focus', handleWindowFocus);

      // Clean up event listener when login completes
      const originalCompleteLogin = completeLogin;
      const wrappedCompleteLogin = (
        result: AuthResponse | null,
        error?: Error
      ) => {
        window.removeEventListener('focus', handleWindowFocus);
        originalCompleteLogin(result, error);
      };

      // Ensure Facebook SDK is loaded before attempting login
      const attemptLogin = () => {
        if (typeof FB === 'undefined') {
          // Initialize Facebook SDK first
          void initializeFb()
            .then(() => {
              if (typeof FB !== 'undefined') {
                performLogin();
              } else {
                wrappedCompleteLogin(
                  null,
                  new Error('Facebook SDK failed to load')
                );
              }
            })
            .catch((error) => {
              wrappedCompleteLogin(null, error);
            });
        } else {
          performLogin();
        }
      };

      const performLogin = () => {
        try {
          // Add timeout for login process
          const loginProcessTimeout = setTimeout(() => {
            console.warn('Facebook login process timeout');
            wrappedCompleteLogin(null, new Error('Facebook login timeout'));
          }, 10000); // 10 seconds timeout for login process

          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          (FB as any).login(
            (res: FBAuthResponse) => {
              clearTimeout(loginProcessTimeout);
              console.log('Facebook login response:', res);
              if (res.status === 'connected' && res.authResponse) {
                // Get user info with timeout
                const userInfoTimeout = setTimeout(() => {
                  console.warn('Facebook user info API timeout');
                  wrappedCompleteLogin(res.authResponse);
                }, 5000); // 5 seconds timeout for user info

                try {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                  (FB as any).api(
                    '/me',
                    { fields: USER_INFO_FIELDS },
                    (userInfo: any) => {
                      clearTimeout(userInfoTimeout);
                      if (userInfo && !userInfo.error) {
                        login(res.authResponse, userInfo);
                        wrappedCompleteLogin(res.authResponse);
                      } else {
                        console.error(
                          FAILED_USER_INFO_MESSAGE,
                          userInfo?.error
                        );
                        wrappedCompleteLogin(
                          null,
                          new Error('Failed to get user info')
                        );
                      }
                    }
                  );
                } catch (userInfoError) {
                  clearTimeout(userInfoTimeout);
                  console.error(
                    'Error during user info API call:',
                    userInfoError
                  );
                  wrappedCompleteLogin(res.authResponse);
                }
              } else {
                console.log('Facebook login failed or cancelled:', res);
                if (res.status === 'not_authorized') {
                  wrappedCompleteLogin(
                    null,
                    new Error('User denied Facebook login')
                  );
                } else if (res.status === 'unknown') {
                  // User cancelled login or closed popup
                  console.log('User cancelled Facebook login');
                  wrappedCompleteLogin(null);
                } else {
                  wrappedCompleteLogin(null);
                }
              }
            },
            { scope: 'email,public_profile' }
          );
        } catch (loginError) {
          console.error('Error during Facebook login:', loginError);
          wrappedCompleteLogin(null, loginError);
        }
      };

      attemptLogin();
    });
  }, [facebookAppId, setIsLoading, login, initializeFb]);

  const logoutFromFacebook = useCallback(() => {
    return new Promise<void>((resolve) => {
      if (typeof FB !== 'undefined') {
        try {
          // Add timeout for logout process
          const logoutTimeout = setTimeout(() => {
            console.warn('Facebook logout timeout - resolving anyway');
            resolve();
          }, 5000); // 5 seconds timeout

          // First check if user is logged in before attempting logout
          (FB as any).getLoginStatus((response: FBAuthResponse) => {
            if (response.status === 'connected') {
              // User is logged in, proceed with logout
              const logoutProcessTimeout = setTimeout(() => {
                clearTimeout(logoutTimeout);
                resolve();
              }, 3000); // 3 seconds timeout for logout process

              try {
                (FB as any).logout(() => {
                  clearTimeout(logoutTimeout);
                  clearTimeout(logoutProcessTimeout);
                  resolve();
                });
              } catch (logoutError) {
                clearTimeout(logoutTimeout);
                clearTimeout(logoutProcessTimeout);
                console.warn('Facebook logout failed:', logoutError);
                resolve();
              }
            } else {
              // User is not logged in, no need to logout
              clearTimeout(logoutTimeout);
              resolve();
            }
          });
        } catch (error) {
          console.warn('Facebook getLoginStatus failed:', error);
          resolve();
        }
      } else {
        resolve();
      }
    });
  }, []);

  const checkFacebookLoginStatus =
    useCallback((): Promise<AuthResponse | null> => {
      if (!facebookAppId) {
        return Promise.resolve(null);
      }

      return new Promise((resolve) => {
        // Add timeout for login status check
        const loginStatusTimeout = setTimeout(() => {
          console.warn('Facebook login status check timeout');
          resolve(null);
        }, 8000); // 8 seconds timeout

        const handleLoginStatus = (res: FBAuthResponse) => {
          clearTimeout(loginStatusTimeout);
          if (res.status === 'connected' && res.authResponse) {
            // User is connected, get their info and update the store
            const userInfoTimeout = setTimeout(() => {
              console.warn('Facebook user info API timeout in status check');
              resolve(res.authResponse);
            }, 5000); // 5 seconds timeout for user info

            try {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
              (FB as any).api(
                '/me',
                { fields: USER_INFO_FIELDS },
                (userInfo: any) => {
                  clearTimeout(userInfoTimeout);
                  if (userInfo && !userInfo.error) {
                    login(res.authResponse, userInfo);
                    resolve(res.authResponse);
                  } else {
                    console.error(FAILED_USER_INFO_MESSAGE, userInfo?.error);
                    resolve(res.authResponse);
                  }
                }
              );
            } catch (userInfoError) {
              clearTimeout(userInfoTimeout);
              console.error(
                'Error during user info API call in status check:',
                userInfoError
              );
              resolve(res.authResponse);
            }
          } else {
            resolve(null);
          }
        };

        if (typeof FB === 'undefined') {
          // Initialize FB first, then check status
          void initializeFb()
            .then(() => {
              if (typeof FB !== 'undefined') {
                try {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                  (FB as any).getLoginStatus(handleLoginStatus);
                } catch (statusError) {
                  clearTimeout(loginStatusTimeout);
                  console.error('Error getting login status:', statusError);
                  resolve(null);
                }
              } else {
                clearTimeout(loginStatusTimeout);
                resolve(null);
              }
            })
            .catch(() => {
              clearTimeout(loginStatusTimeout);
              resolve(null);
            });
        } else {
          try {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            (FB as any).getLoginStatus(handleLoginStatus);
          } catch (statusError) {
            clearTimeout(loginStatusTimeout);
            console.error('Error getting login status:', statusError);
            resolve(null);
          }
        }
      });
    }, [facebookAppId, initializeFb, login]);

  const restoreUserSession = useCallback(async () => {
    if (!facebookAppId || isRestoringSessionRef.current) {
      return null;
    }

    // Since initializeFb already handles session restoration when autoRestore is true,
    // we don't need to call checkFacebookLoginStatus again
    if (autoRestore) {
      return null;
    }

    try {
      isRestoringSessionRef.current = true;
      const result = await checkFacebookLoginStatus();
      isRestoringSessionRef.current = false;
      return result;
    } catch (error) {
      console.error('Failed to restore user session:', error);
      isRestoringSessionRef.current = false;
      return null;
    }
  }, [facebookAppId, checkFacebookLoginStatus, autoRestore]);

  // Auto-restore user session if requested (only when not handled by initializeFb)
  useEffect(() => {
    if (autoRestore && !isRestoringSessionRef.current) {
      // The initializeFb already handles session restoration when autoRestore is true
      // This useEffect is kept for backward compatibility but won't trigger duplicate calls
      return;
    }
  }, [autoRestore]);

  // Cleanup timeouts on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      clearAllFacebookTimeouts();
    };
  }, []);

  return {
    initializeFb,
    loginWithFacebook,
    logoutFromFacebook,
    checkFacebookLoginStatus,
    restoreUserSession,
  };
};
